import * as ko from "knockout";
import PromiseRequest from "../../../../../req/PromiseRequest";
import {IExportAttribute, IMessageData, IObjectType, IRuleset} from "../../../../../index";
import {KOSubscriptionTracker} from "../../../../../util/KOSubscriptionTracker";
import {EXCHANGE_VERSIONS, ObjectTypeId, SNACKBAR_DURATION_MEDIUM} from "../../../../../constants";
import {global} from "../../../../../appContext";
import {CustomLabel} from "../MutableAbstract";
import {nsExportTemplate} from "../../../fn";
import {createErrorMessage} from "../../../../../util/misc";
import {IExportFlowMutable, IExportTemplateMutable} from "../../../index";
import {IDialogOptions} from "../../../../dialog/index";
import {TMailFlowAttribute} from "../index";
import {REQUEST_MESSAGE} from "../../../../../constants/Language";
import {ButtonManager} from "../../../../../util/ButtonManager";
import {getCancelButton, getContinueButton} from "../../../../../constants/RouteDefinitions";
import createExportFlowMutable = nsExportTemplate.createExportFlowMutable;
import PRIMARY_SMTP_DOMAIN_EXAMPLE = nsExportTemplate.PRIMARY_SMTP_DOMAIN_EXAMPLE;
import {IRequestObject} from "../../../../../req/index";
import exchangeHtml from './exchange.html?raw';

export let template = exchangeHtml;

interface IParams {
    dialog: IDialogOptions
    exporttemplate: IExportTemplateMutable,
    onSubmit: (data: IExportFlowMutable[]) => void,
}

export class viewModel {
    private ObjectTypeId = ObjectTypeId;
    private data: KnockoutObservableArray<IExportFlowMutable> = ko.observableArray();
    private ExportAttributes!: IExportAttribute[];
    private readonly $ruleset: KnockoutObservable<IRuleset | null> = ko.observable(null);
    private readonly ExchangeVersions = [...EXCHANGE_VERSIONS, CustomLabel];
    private readonly exporttemplate: IExportTemplateMutable;
    private readonly isReady: KnockoutObservable<boolean | null> = ko.observable(null);
    private readonly kosub: KOSubscriptionTracker = new KOSubscriptionTracker();
    private readonly messageData: KnockoutObservable<IMessageData | null> = ko.observable(null);
    private readonly objecttype: KnockoutObservable<IObjectType | null>;
    private readonly req: PromiseRequest = global.getPromiseRequest('getexportattributes');
    private reqobj_getexportattributes!: IRequestObject<IExportAttribute[]>;
    private readonly pageButtons: ButtonManager = new ButtonManager()

    constructor(private params: IParams) {
        this.exporttemplate = params.exporttemplate;
        this.objecttype = this.exporttemplate.objecttype;

        this.pageButtons.add(getCancelButton(() => this.params.dialog.doClose?.()))
        this.pageButtons.add(getContinueButton({
            icon: 'fa-check',
            label: 'add',
            onClick: () => this.onSubmit(),
        }))

        this.init();
    }

    private init() {
        this.isReady(false);
        this.messageData({message: REQUEST_MESSAGE});
        this.reqobj_getexportattributes?.promise.abort()
        this.reqobj_getexportattributes = nsExportTemplate.getExportAttributes(this.exporttemplate.syncstep()!.syncstep.id, this.objecttype()!.id)
        this.reqobj_getexportattributes.request
            .then((data: IExportAttribute[]) => {
                this.ExportAttributes = data;
                [
                    'disableOwa',
                    'DomainController',
                    'ExtraSMTP',
                    'mailPath',
                    'mailStore',
                    'mailVersion',
                    'primarySMTPDomain',
                    'proxyAddresses',
                    'TargetAddress',
                ].forEach(k => {
                    const ea = this.ExportAttributes.find(o => o.name == k);
                    if (!ea) {
                        return;
                    }
                    const efm = createExportFlowMutable();
                    this.data.push(efm);
                    efm.$original.targetattributetype = ea.type;
                    efm.$original.targetattribute = ea.name;
                    efm.targetattribute(ea);
                })
                this.getFlow('primarySMTPDomain')?.expression(PRIMARY_SMTP_DOMAIN_EXAMPLE);
                this.getFlow('disableOwa')?.expression('False');
                this.getFlow('mailVersion')?.expression(EXCHANGE_VERSIONS[0] ?? '');
                this.messageData(null);
                this.isReady(true);
            })
            .catch((reason: string) => {
                this.messageData(createErrorMessage(String(reason)))
            })
        ;
    }

    dispose() {
        this.kosub.unsuscribeAll();
        this.req.abort();
        this.reqobj_getexportattributes?.promise.abort()
    }

    private getFlow(targetattribute: TMailFlowAttribute): IExportFlowMutable | undefined {
        return this.data().find(o => o.targetattribute()?.name == targetattribute);
    }

    private onSubmit() {
        const data = this.getSubmitData();
        const mailVersionFlow = this.getFlow('mailVersion');
        this.params.onSubmit(data);
        global.snackbarData({
            message: `added subject|s:Exchange ((${mailVersionFlow?.expression() ?? ''}))`,
            undo: () => data.forEach(o => this.params.exporttemplate.exportflow.remove(o)),
            duration: SNACKBAR_DURATION_MEDIUM,
        });
        this.params.dialog.doClose?.();
    }

    private getSubmitData(): IExportFlowMutable[] {
        let mailAttributes: TMailFlowAttribute[];
        const mailVersionFlow = this.getFlow('mailVersion');
        switch (mailVersionFlow?.expression()) {
            case CustomLabel:
                mailAttributes = [
                    'TargetAddress',
                    'proxyAddresses',
                ];
                break;
            case 'Remote':
                mailAttributes = [
                    'mailVersion',
                    'mailPath',
                    'mailStore',
                    'DomainController',
                    'TargetAddress',
                ];
                break;
            default:
                mailAttributes = [
                    'mailVersion',
                    'mailPath',
                    'DomainController',
                    'mailStore',
                    'primarySMTPDomain',
                    'ExtraSMTP',
                    'disableOwa',
                ];
                break;
        }
        return mailAttributes
            .map(k => this.data().find(o => o.targetattribute()?.name == k))
            .filter((efm): efm is IExportFlowMutable => !!efm)
            .map(efm => {
                switch (efm.targetattribute()?.name) {
                    case 'disableOwa':
                    case 'proxyAddresses':
                        break;
                    default: {
                        const ruleset = this.$ruleset();
                        if (ruleset) {
                            efm.ruleset(ruleset);
                        }
                        break;
                    }
                }
                return efm;
            });
    }

}
